import { Checkbox, Menu, Table } from '@mantine/core';
import { IconShoppingBag, IconTaxPound } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KeyedRow } from '../../../components/KeyedRow';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { formatToCurrency } from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type {
	ProductPriceDto,
	UpsertProductPriceDto,
} from '../types/ProductPriceDto';
import { EditProductPriceModal } from './EditProductPriceModal';

interface PriceChangeRowProps {
	originalProductPrice: ProductPriceDto;
	isSelected: boolean;
	onToggle: () => void;
}

export function PriceChangeRow({
	originalProductPrice,
	isSelected,
	onToggle,
}: PriceChangeRowProps) {
	const { organisation } = useParams();

	const navigate = useNavigate();

	const [productPrice, setProductPrice] = useState(originalProductPrice);
	const encodedUri = encodeURIComponent(productPrice.skuid);

	const editModal = useModal();

	return (
		<>
			<EditProductPriceModal
				isOpened={editModal.isOpen}
				close={editModal.close}
				productPrice={convertProductPrice(productPrice)}
				updateProductPriceRecord={(p) => {
					setProductPrice(p);
				}}
				encodedSkuid={encodedUri}
				branchId={productPrice.branchId.toString()}
			/>
			<KeyedRow>
				<Table.Td>
					<Checkbox
						color={'medepos.1'}
						checked={isSelected}
						onChange={onToggle}
					/>
				</Table.Td>
				<Table.Td>{undefinedCheck(productPrice.skuid)}</Table.Td>
				<Table.Td>{undefinedCheck(productPrice.description)}</Table.Td>
				<Table.Td>{productPrice.minStock}</Table.Td>
				<Table.Td>{productPrice.maxStock}</Table.Td>
				<Table.Td>{undefinedCheck(productPrice.branchName)}</Table.Td>
				<Table.Td>{formatToCurrency(productPrice.incVatPrice)}</Table.Td>
				<Table.Td>{productPrice.stockLevel}</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconShoppingBag {...defaultIconStyle} />}
							onClick={() =>
								navigate(`/${organisation}/stock/products/${encodedUri}`)
							}
						>
							View Product
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTaxPound {...defaultIconStyle} />}
							onClick={() => editModal.open()}
						>
							Edit Price & Stock Level
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
		</>
	);
}

export const convertProductPrice = (
	productPrice: ProductPriceDto,
): UpsertProductPriceDto => {
	return {
		skuid: productPrice.skuid,
		branchId: productPrice.branchId,
		incVatPrice: productPrice.incVatPrice,
		stockLevel: productPrice.stockLevel,
		branchName: productPrice.branchName,
		minStock: productPrice.minStock,
		maxStock: productPrice.maxStock
	};
};
